import type { ButtonHTMLAttributes } from 'react'
import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost'
export type ButtonSize = 'sm' | 'md'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'border border-transparent bg-brand text-brand-fg hover:bg-brand-hover',
  secondary: 'border border-app-border bg-transparent text-app-text hover:bg-app-card',
  danger:
    'border border-stage-rejected bg-transparent text-stage-rejected-text hover:bg-stage-rejected/10',
  ghost: 'border border-transparent bg-transparent text-app-text hover:bg-app-card',
}

// 44x44px minimum touch target per the a11y rule in docs/04-ui-ux.md.
const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-11 px-3 text-sm',
  md: 'h-11 px-4 text-sm',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-button font-medium transition-colors duration-150 ease-out disabled:pointer-events-none disabled:opacity-50',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    />
  ),
)
Button.displayName = 'Button'
